<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              title="Product Upload History"
              subtitle="View and manage all bulk product upload activities"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <v-btn
                      color="info"
                      elevation="1"
                      small
                      @click="getStatistics"
                      :loading="isLoadingStats"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-chart-line</v-icon>
                      Refresh Stats
                    </v-btn>
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/products/upload"
                      class="px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      New Upload
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Statistics Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-chart-box</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Upload Statistics</span
                    >
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-2">
              <v-row>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <v-icon size="24" color="primary"
                        >mdi-cloud-upload</v-icon
                      >
                      <div class="text-h6 font-weight-bold">
                        {{ statistics.total_uploads || 0 }}
                      </div>
                      <div class="text-caption">Total Uploads</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <v-icon size="24" color="success"
                        >mdi-check-circle</v-icon
                      >
                      <div class="text-h6 font-weight-bold text-success">
                        {{ statistics.successful_uploads || 0 }}
                      </div>
                      <div class="text-caption">Successful</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <v-icon size="24" color="error">mdi-alert-circle</v-icon>
                      <div class="text-h6 font-weight-bold text-error">
                        {{ statistics.failed_uploads || 0 }}
                      </div>
                      <div class="text-caption">Failed</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <v-icon size="24" color="info"
                        >mdi-package-variant</v-icon
                      >
                      <div class="text-h6 font-weight-bold text-info">
                        {{ statistics.total_products_uploaded || 0 }}
                      </div>
                      <div class="text-caption">Products Created</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>

            <v-divider class="mx-4"></v-divider>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Upload History</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Uploads"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Filename, notes..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.status"
                    :items="statusOptions"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- User Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.user_id"
                    :items="users"
                    label="Uploaded By"
                    prepend-inner-icon="mdi-account"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Date Range -->
                <v-col cols="12" md="3">
                  <v-menu
                    v-model="showDatePicker"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="filter.date_range"
                        label="Date Range"
                        prepend-inner-icon="mdi-calendar"
                        outlined
                        dense
                        readonly
                        clearable
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="(filter.date_range = null), getData()"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.date_range"
                      range
                      @change="getData"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Upload History Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Upload History</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-cloud-upload</v-icon>
                      {{ count }} {{ count === 1 ? "Upload" : "Uploads" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading upload history..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="uploads"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- File Info Column -->
                <template #[`item.file_info`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2" color="info">
                      <v-icon color="white" size="18">mdi-file-excel</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        {{ item.original_filename || "Unknown File" }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ formatDate(item.created_at) }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Status Column -->
                <template #[`item.status`]="{ item }">
                  <v-chip :color="getStatusColor(item.status)" small dark>
                    <v-icon left x-small>
                      {{ getStatusIcon(item.status) }}
                    </v-icon>
                    {{ formatStatus(item.status) }}
                  </v-chip>
                </template>

                <!-- Results Column -->
                <template #[`item.results`]="{ item }">
                  <div>
                    <div class="d-flex align-center mb-1">
                      <v-icon small color="success" class="mr-1"
                        >mdi-check</v-icon
                      >
                      <span class="text-caption"
                        >{{ item.successful_rows || 0 }} created</span
                      >
                    </div>
                    <div class="d-flex align-center mb-1">
                      <v-icon small color="error" class="mr-1"
                        >mdi-close</v-icon
                      >
                      <span class="text-caption"
                        >{{ item.failed_rows || 0 }} failed</span
                      >
                    </div>
                    <div class="d-flex align-center">
                      <v-icon small color="info" class="mr-1">mdi-table</v-icon>
                      <span class="text-caption"
                        >{{ item.total_rows || 0 }} total</span
                      >
                    </div>
                  </div>
                </template>

                <!-- Progress Column -->
                <template #[`item.progress`]="{ item }">
                  <div>
                    <v-progress-linear
                      :value="getSuccessRate(item)"
                      :color="getSuccessRateColor(item)"
                      height="6"
                      rounded
                      class="mb-1"
                    ></v-progress-linear>
                    <div class="text-caption text-center">
                      {{ getSuccessRate(item) }}%
                    </div>
                  </div>
                </template>

                <!-- User Column -->
                <template #[`item.user_info`]="{ item }">
                  <div v-if="item.user">
                    <div class="text-body-2 font-weight-medium">
                      {{ item.user.first_name }} {{ item.user.last_name }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ item.user.email }}
                    </div>
                  </div>
                  <div v-else class="text-caption grey--text">
                    User not found
                  </div>
                </template>

                <!-- Notes Column -->
                <template #[`item.notes`]="{ item }">
                  <div v-if="item.notes" class="text-caption">
                    {{
                      item.notes.length > 50
                        ? item.notes.substring(0, 50) + "..."
                        : item.notes
                    }}
                  </div>
                  <div v-else class="text-caption grey--text">No notes</div>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          x-small
                          color="primary"
                          @click="viewDetails(item)"
                          class="mr-1"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon x-small>mdi-eye</v-icon>
                        </v-btn>
                      </template>
                      <span>View Details</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="item.failed_rows > 0">
                      <template #activator="{ on, attrs }">
                        <v-btn
                          x-small
                          color="warning"
                          @click="downloadErrors(item.id)"
                          class="mr-1"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon x-small>mdi-download</v-icon>
                        </v-btn>
                      </template>
                      <span>Download Errors</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          x-small
                          color="error"
                          @click="confirmDelete(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon x-small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Delete Upload</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-cloud-upload-outline</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">
                      No Upload History Found
                    </h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No uploads match your current filters. Try adjusting your search criteria."
                          : "You haven't uploaded any products yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters"
                      color="primary"
                      to="/products/upload"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Upload Products
                    </v-btn>
                    <v-btn
                      v-else
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">
                Are you sure you want to delete this upload record?
              </h3>

              <div
                class="upload-info-card pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3" color="info">
                    <v-icon color="white" size="24">mdi-file-excel</v-icon>
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      {{
                        uploadToDelete ? uploadToDelete.original_filename : ""
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      Status:
                      {{
                        uploadToDelete
                          ? formatStatus(uploadToDelete.status)
                          : ""
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      Products:
                      {{ uploadToDelete ? uploadToDelete.successful_rows : 0 }}
                      created,
                      {{ uploadToDelete ? uploadToDelete.failed_rows : 0 }}
                      failed
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will remove the upload
                record from history.
              </p>
              <p class="text-body-2 grey--text">
                The products created from this upload will remain in your
                inventory.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="showDeleteDialog = false"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="deleteUpload"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Record
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Upload Details Dialog -->
      <v-dialog v-model="showDetailsDialog" max-width="800">
        <v-card v-if="selectedUpload">
          <v-card-title class="primary white--text">
            <v-icon color="white" class="mr-2">mdi-information</v-icon>
            Upload Details
          </v-card-title>

          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <h4 class="mb-2">File Information</h4>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Original Filename</v-list-item-title>
                      <v-list-item-subtitle>{{
                        selectedUpload.original_filename
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Upload Date</v-list-item-title>
                      <v-list-item-subtitle>{{
                        formatDate(selectedUpload.created_at)
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Status</v-list-item-title>
                      <v-list-item-subtitle>
                        <status-chip :color="getStatusColor(selectedUpload.status)" :label="String(formatStatus(selectedUpload.status))" />
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col cols="12" md="6">
                <h4 class="mb-2">Results Summary</h4>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Total Rows</v-list-item-title>
                      <v-list-item-subtitle>{{
                        selectedUpload.total_rows
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Successful</v-list-item-title>
                      <v-list-item-subtitle class="text-success">{{
                        selectedUpload.successful_rows
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Failed</v-list-item-title>
                      <v-list-item-subtitle class="text-error">{{
                        selectedUpload.failed_rows
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>

            <div v-if="selectedUpload.notes" class="mt-4">
              <h4 class="mb-2">Notes</h4>
              <v-card outlined>
                <v-card-text>
                  {{ selectedUpload.notes }}
                </v-card-text>
              </v-card>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              v-if="selectedUpload.failed_rows > 0"
              color="warning"
              @click="downloadErrors(selectedUpload.id)"
              class="mr-2"
            >
              <v-icon left>mdi-download</v-icon>
              Download Errors
            </v-btn>
            <v-btn outlined @click="showDetailsDialog = false"> Close </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "ProductUploadHistory",
  data() {
    return {
      // Data table
      uploads: [],
      count: 0,
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      jumpToPage: 1,
      paginationLength: 0,

      // Filters
      filter: {
        search_keyword: null,
        status: null,
        user_id: null,
        date_range: null,
      },

      // Filter options
      statusOptions: [
        { text: "Pending", value: "pending" },
        { text: "Processing", value: "processing" },
        { text: "Completed", value: "completed" },
        { text: "Failed", value: "failed" },
      ],
      users: [],

      // Statistics
      statistics: {},
      isLoadingStats: false,

      // Loading states
      isLoading: false,
      isDeleting: false,

      // UI states
      showDeleteDialog: false,
      showDetailsDialog: false,
      showDatePicker: false,
      uploadToDelete: null,
      selectedUpload: null,

      // Table headers
      headers: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "50px",
          align: "center",
        },
        {
          text: "File Info",
          value: "file_info",
          sortable: false,
          width: "200px",
        },
        {
          text: "Status",
          value: "status",
          sortable: false,
          width: "120px",
        },
        {
          text: "Results",
          value: "results",
          sortable: false,
          width: "140px",
        },
        {
          text: "Progress",
          value: "progress",
          sortable: false,
          width: "120px",
        },
        {
          text: "Uploaded By",
          value: "user_info",
          sortable: false,
          width: "150px",
        },
        {
          text: "Notes",
          value: "notes",
          sortable: false,
          width: "200px",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "110px",
          align: "center",
        },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.status ||
        this.filter.user_id ||
        this.filter.date_range
      );
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
  },

  mounted() {
    this.jumpToPage = this.page;
    this.getData();
    this.getUsers();
    this.getStatistics();
  },

  methods: {
    async getData() {
      console.log("Get Upload History Data");
      this.isLoading = true;

      try {
        const params = {
          ...this.filter,
          page: this.page,
          rowsPerPage: this.itemsPerPage,
        };

        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === "") {
            delete params[key];
          }
        });

        const response = await axios.get("/product_uploads", { params });

        if (response.data.success) {
          this.uploads = response.data.data;
          this.count = response.data.count;
          this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to fetch upload history",
          });
        }
      } catch (error) {
        console.error(error);
        this.$store.dispatch("errors/setError", {
          message: "Error loading upload history data",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async getUsers() {
      try {
        const response = await axios.get("/users", {
          params: { is_active: 1, show_deleted: 0, rowsPerPage: -1 },
        });
        if (response.data.success) {
          this.users = response.data.data.map((user) => ({
            text: `${user.first_name} ${user.last_name} (${user.email})`,
            value: user.id,
          }));
        }
      } catch (error) {
        console.log("Error loading users:", error);
      }
    },

    async getStatistics() {
      this.isLoadingStats = true;
      try {
        const response = await axios.get("/product_uploads/statistics");
        if (response.data.success) {
          this.statistics = response.data.data;
        }
      } catch (error) {
        console.log("Error loading statistics:", error);
      } finally {
        this.isLoadingStats = false;
      }
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.status) count++;
      if (this.filter.user_id) count++;
      if (this.filter.date_range) count++;
      return count;
    },

    clearAllFilters() {
      this.filter = {
        search_keyword: null,
        status: null,
        user_id: null,
        date_range: null,
      };
      this.page = 1;
      this.getData();
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
      } else {
        this.jumpToPage = this.page;
      }
    },

    confirmDelete(upload) {
      this.uploadToDelete = upload;
      this.showDeleteDialog = true;
    },

    async deleteUpload() {
      if (!this.uploadToDelete) return;

      this.isDeleting = true;
      try {
        const response = await axios.delete(
          `/products/upload/${this.uploadToDelete.id}`
        );

        if (response.data.success) {
          this.$toast.success("Upload record deleted successfully");
          this.getData();
          this.getStatistics();
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to delete upload record",
          });
        }
      } catch (error) {
        console.error(error);
        this.$store.dispatch("errors/setError", {
          message: "Error deleting upload record",
        });
      } finally {
        this.isDeleting = false;
        this.showDeleteDialog = false;
        this.uploadToDelete = null;
      }
    },

    viewDetails(upload) {
      this.selectedUpload = upload;
      this.showDetailsDialog = true;
    },

    async downloadErrors(uploadId) {
      try {
        const response = await axios.get(
          `/products/upload/${uploadId}/errors`,
          {
            responseType: "blob",
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `product_upload_errors_${uploadId}_${new Date()
            .toISOString()
            .slice(0, 10)}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.$toast.success("Error report downloaded successfully");
      } catch (error) {
        console.error(error);
        this.$store.dispatch("errors/setError", {
          message: "Error downloading error report",
        });
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },

    formatStatus(status) {
      return status
        ? status.charAt(0).toUpperCase() + status.slice(1)
        : "Unknown";
    },

    getStatusColor(status) {
      switch (status) {
        case "completed":
          return "success";
        case "processing":
          return "info";
        case "pending":
          return "grey";
        case "failed":
          return "error";
        default:
          return "grey";
      }
    },

    getStatusIcon(status) {
      switch (status) {
        case "completed":
          return "mdi-check-circle";
        case "processing":
          return "mdi-loading";
        case "pending":
          return "mdi-clock";
        case "failed":
          return "mdi-alert-circle";
        default:
          return "mdi-help-circle";
      }
    },

    getSuccessRate(upload) {
      if (!upload.total_rows || upload.total_rows === 0) return 0;
      return Math.round((upload.successful_rows / upload.total_rows) * 100);
    },

    getSuccessRateColor(upload) {
      const rate = this.getSuccessRate(upload);
      if (rate >= 80) return "success";
      if (rate >= 60) return "warning";
      return "error";
    },
  },
};
</script>

<style scoped>
/* Compact Layout Adjustments */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling - Compact Version */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Compact Filter Cards */
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Compact Button Styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

/* Compact Pagination */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* Compact Chip Styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

/* Compact Avatar Styling */
.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Progress Enhancements */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

/* Responsive Design */
@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }

  .v-card-title {
    padding: 8px 12px 4px !important;
  }

  .v-card-text {
    padding: 4px 12px 8px !important;
  }

  .v-card-actions {
    padding: 4px 12px !important;
  }

  .v-card-subtitle {
    padding: 4px 12px !important;
  }
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Upload info card */
.upload-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.upload-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
